/*
    Copyright NetFoundry Inc.

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    https://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import {Injectable} from '@angular/core';
import {isEmpty, defer} from "lodash";
import { HttpBackend, HttpClient } from "@angular/common/http";
import {BehaviorSubject, firstValueFrom, map, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {GrowlerService} from "../features/messaging/growler.service";
import {GrowlerModel} from "../features/messaging/growler.model";

const DEFAULTS = {
    "session": {},
    "edgeControllers": [],
    "editable": true,
    "update": false,
    "location": "../ziti",
    "protocol": "http",
    "host": "localhost",
    "port": 1408,
    "portTLS": 8443,
    "rejectUnauthorized": false,
    "mail": {
        "host": "",
        "port": 25,
        "secure": false,
        "auth": {
            "user": "",
            "pass": ""
        }
    },
    "from": "",
    "to": "",
}

@Injectable({
    providedIn: 'root'
})
export abstract class SettingsServiceClass {

    name = "settings";
    settings: any = {};
    versionData: any = {};
    settingsChange = new BehaviorSubject<any>({})
    rejectUnauthorized = false;
    port = DEFAULTS.port;
    portTLS = DEFAULTS.portTLS;
    apiVersions: any[] = [];
    capabilities: string[] = [];
    edgeOidcPath: string | null = null;
    protocol = DEFAULTS.protocol;
    host = DEFAULTS.host;
    httpClient: HttpClient;
    zitiSemver = '';
    allowControllerAdd = false;

    public supportedFeatures: any = {
        organization: false,
        tags: true,
        customFields: false,
        mailer: false,
        recipes: false
    }

    constructor(protected httpBackend: HttpBackend, protected growlerService: GrowlerService) {
        this.httpClient = new HttpClient(httpBackend);
    }

    public abstract init();
    public abstract controllerSave(name: string, url: string);
    public abstract initApiVersions(url);
    public abstract loadSettings();
    public abstract hasSession();

    // True when the selected controller advertises native OIDC authentication
    public oidcAvailable(): boolean {
        return (this.capabilities || []).includes('OIDC_AUTH') && !isEmpty(this.edgeOidcPath);
    }

    public setOidcCapabilities(versionData: any) {
        this.capabilities = versionData?.capabilities || [];
        this.edgeOidcPath = versionData?.apiVersions?.['edge-oidc']?.v1?.path || null;
    }

    public clearOidcCapabilities() {
        this.capabilities = [];
        this.edgeOidcPath = null;
    }

    // HA Controller methods
    public abstract isHAEnabled(): boolean;
    public abstract getHAControllers(): any[];
    public abstract getActiveSessions(): Map<string, string>;
    public abstract getJwtToken(): string | null;
    public abstract hasValidJwtToken(): boolean;
    public abstract setJwtToken(token: string): void;

    public get() {
        const tmp = localStorage.getItem('ziti.settings');
        if (tmp) {
            this.settings = JSON.parse(tmp);
        } else {
            this.settings = {...DEFAULTS};
            localStorage.setItem('ziti.settings', JSON.stringify(this.settings));
        }
        this.settingsChange.next(this.settings)
    }

    public set(data: any) {
        this.settings = data;
        localStorage.setItem('ziti.settings', JSON.stringify(data));
        this.settingsChange.next(this.settings);
    }

    public version() {
        this.versionData = localStorage.getItem('ziti.version');
        this.settings = {...this.settings, version: this.versionData};
        this.settingsChange.next(this.settings)
    }

    public getHttpOptions() {
        const options: any = {
            headers: {
                accept: '*',
            },
            params: {},
            responseType: 'text' as const,
        };
        return options;
    }

    public addContoller(name: string, url: string) {
        const trimmedName = name.trim();
        const trimmedUrl = url.trim();
        if (trimmedName.length == 0 || trimmedUrl.length == 0) {
            let growlerData = new GrowlerModel(
              'error',
              'Error',
              'Name and URL required',
            );
            this.growlerService.show(growlerData);
            return Promise.reject({ error: 'Name and URL required' });
        } else {
            return this.controllerSave(trimmedName, trimmedUrl);
        }
    }

    // HA Controller management methods
    public abstract addHAController(url: string, name: string): void;
    public abstract removeHAController(url: string): void;
    public abstract setControllerSession(url: string, sessionToken: string): void;
    public abstract getControllerSession(url: string): string | null;
    public abstract clearControllerSessions(): void;
    public abstract clearJwtToken(): void;
}
